import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, DateInput, Switch, TextArea, TextInput } from '@origo/origo-ui';
import type { DispensasjonFormValues, FieldChangeHandler } from '../../../types';
import { dispensasjonTypeOptions, dispensasjonFraOptions } from '../../../codelists/dispensasjonType';
import FormFooterActions from '../../plan-info/components/FormFooterActions';
import styles from './DispensationForm.module.css';

export type DispensationFieldErrors = Partial<Record<keyof DispensasjonFormValues, string>>;

interface DispensationFormProps {
    values: DispensasjonFormValues;
    fieldErrors?: DispensationFieldErrors;
    onChange: FieldChangeHandler<DispensasjonFormValues>;
    /** Skjemaet rendrer egen knapperad når `onSave`/`onCancel` er gitt (inline
     *  redigering); opprett-dialogen eier footeren selv og lar dem være undefined. */
    onCancel?: () => void;
    onSave?: () => void;
    saveDisabled?: boolean;
    isSaving?: boolean;
    /** Åpner sidepanelet med tilknyttede dokumenter (F12.4). Kun satt ved redigering
     *  — en dispensasjon som ikke er opprettet ennå har ingen dokumenter. */
    onShowDocuments?: () => void;
    /** Antall dokumenter knyttet til dispensasjonen, vist på knappen. */
    documentCount?: number;
    /** Slett-handling i footeren (#14731). Kun satt ved redigering. */
    onDelete?: () => void;
}

const DispensationForm: React.FC<DispensationFormProps> = ({
    values,
    fieldErrors = {},
    onChange,
    onCancel,
    onSave,
    saveDisabled,
    isSaving,
    onShowDocuments,
    documentCount,
    onDelete,
}) => {
    const { t } = useTranslation();

    return (
        <div className={styles.form}>
            <Select
                label={t('dispensations.fields.dispensasjonType')}
                placeholder={t('dispensations.fields.dispensasjonTypePlaceholder')}
                value={values.dispensasjonType}
                error={fieldErrors.dispensasjonType}
                onChange={e => onChange('dispensasjonType', e.target.value)}
                options={dispensasjonTypeOptions(t)}
            />
            <Select
                label={t('dispensations.fields.dispensasjonFra')}
                placeholder={t('dispensations.fields.dispensasjonFraPlaceholder')}
                value={values.dispensasjonFra}
                error={fieldErrors.dispensasjonFra}
                onChange={e => onChange('dispensasjonFra', e.target.value)}
                options={dispensasjonFraOptions(t)}
            />
            <div className={styles.twoColumn}>
                <DateInput
                    label={t('dispensations.fields.vedtaksdato')}
                    value={values.vedtaksdato}
                    error={fieldErrors.vedtaksdato}
                    onChange={e => onChange('vedtaksdato', e.target.value)}
                />
                <TextInput
                    label={t('dispensations.fields.saksnummer')}
                    placeholder={t('dispensations.fields.saksnummerPlaceholder')}
                    value={values.saksnummer}
                    error={fieldErrors.saksnummer}
                    onChange={e => onChange('saksnummer', e.target.value)}
                />
            </div>
            <div className={styles.twoColumn}>
                <Switch
                    label={t('dispensations.fields.innvilgetDispensasjon')}
                    checked={values.innvilgetDispensasjon}
                    onChange={v => onChange('innvilgetDispensasjon', v)}
                />
                <Switch
                    label={t('dispensations.fields.varigDispensasjon')}
                    checked={values.varigDispensasjon}
                    onChange={v => onChange('varigDispensasjon', v)}
                />
            </div>
            {!values.varigDispensasjon && (
                <div className={styles.twoColumn}>
                    <DateInput
                        label={t('dispensations.fields.varighetFra')}
                        value={values.varighetFra}
                        error={fieldErrors.varighetFra}
                        onChange={e => onChange('varighetFra', e.target.value)}
                    />
                    <DateInput
                        label={t('dispensations.fields.varighetTil')}
                        value={values.varighetTil}
                        error={fieldErrors.varighetTil}
                        onChange={e => onChange('varighetTil', e.target.value)}
                    />
                </div>
            )}
            <div className={styles.divider} aria-hidden />
            <TextArea
                label={t('dispensations.fields.begrunnelse')}
                placeholder={t('dispensations.fields.begrunnelsePlaceholder')}
                value={values.begrunnelse}
                error={fieldErrors.begrunnelse}
                onChange={e => onChange('begrunnelse', e.target.value)}
                rows={3}
            />
            <TextArea
                label={t('dispensations.fields.beskrivelse')}
                placeholder={t('dispensations.fields.beskrivelsePlaceholder')}
                value={values.beskrivelse}
                error={fieldErrors.beskrivelse}
                onChange={e => onChange('beskrivelse', e.target.value)}
                rows={3}
            />
            {onShowDocuments && (
                <Button
                    variant="secondary"
                    size="sm"
                    className={styles.documentsButton}
                    onClick={onShowDocuments}
                >
                    {t('dispensations.documents.button', { count: documentCount ?? 0 })}
                </Button>
            )}
            {onSave && onCancel && (
                <FormFooterActions
                    onCancel={onCancel}
                    onSave={onSave}
                    onDelete={onDelete}
                    deleteLabel={t('dispensations.delete.action')}
                    saveDisabled={saveDisabled}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default DispensationForm;
